import React, { useState } from "react";
import dayjs from "dayjs";
import { CalendarDays, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";

/**
 * A reusable single date picker component with customizable label
 *
 * @param {string} label - The label text to display above the date picker
 * @param {dayjs.Dayjs|null} value - The selected date value (dayjs object)
 * @param {function} onChange - Callback function when date changes
 * @param {string} placeholder - Placeholder text for the input
 * @param {string} format - Date format (default: "MM/DD/YYYY") - kept for API compat, display uses date-fns
 * @param {boolean} disablePastDates - Whether to disable past dates (default: false)
 * @param {dayjs.Dayjs|null} minDate - Minimum selectable date
 * @param {dayjs.Dayjs|null} maxDate - Maximum selectable date
 * @param {string} className - Additional CSS classes
 * @param {boolean} required - Whether the field is required (shows * indicator)
 * @param {boolean} disabled - Whether the input is disabled
 * @param {boolean} allowClear - Whether to show clear button (default: true)
 * @param {boolean} isInvalid - Whether the field has validation errors (shows red border)
 * @param {function} onFocus - Callback function when field is focused
 *
 * @example
 * <SingleDatePicker
 *   label="Event Date"
 *   value={eventDate}
 *   onChange={setEventDate}
 *   placeholder="Select event date"
 *   disablePastDates={true}
 *   required={true}
 *   isInvalid={isFieldInvalid("eventDate")}
 *   onFocus={() => handleFieldFocus("eventDate")}
 * />
 */
const SingleDatePicker = ({
  label,
  value,
  onChange,
  placeholder = "Select date",
  format: dateFormat = "MM/DD/YYYY",
  disablePastDates = false,
  minDate = null,
  maxDate = null,
  className = "",
  required = false,
  disabled = false,
  allowClear = true,
  isInvalid = false,
  onFocus,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Convert dayjs value to native Date for Calendar component
  const selectedDate = value && dayjs.isDayjs(value) ? value.toDate() : value instanceof Date ? value : null;

  // Build disabled date function for Calendar
  const isDateDisabled = (date) => {
    if (disablePastDates && date < dayjs().startOf("day").toDate()) {
      return true;
    }
    if (minDate && date < dayjs(minDate).startOf("day").toDate()) {
      return true;
    }
    if (maxDate && date > dayjs(maxDate).startOf("day").toDate()) {
      return true;
    }
    return false;
  };

  const handleSelect = (date) => {
    if (onChange) {
      // Return dayjs object to preserve existing consumer API
      onChange(date ? dayjs(date) : null);
    }
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) {
      onChange(null);
    }
  };

  // Format display text using date-fns
  const getDisplayText = () => {
    if (!selectedDate) return null;
    try {
      return format(selectedDate, "PPP");
    } catch {
      return null;
    }
  };

  const displayText = getDisplayText();

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </Label>
      )}
      <div className="flex w-full items-center" onFocus={onFocus}>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className={cn(
                // flex-1 + min-w-0 lets the trigger shrink within the row so the
                // clear "X" stays inside the container bounds when the cell is
                // narrow (instead of overflowing the right edge). overflow-hidden
                // + truncate keep the date label from pushing width past the cell.
                "h-7 min-w-0 flex-1 justify-start overflow-hidden rounded-md border-edge-input bg-white text-left font-normal text-ink",
                !selectedDate && "text-ink-muted",
                isInvalid && "border-red-500",
                disabled && "cursor-not-allowed opacity-50",
              )}
            >
              <CalendarDays className="mr-2 h-4 w-4 shrink-0" />
              <span className="truncate">
                {displayText || <span className="text-ink-muted">{placeholder}</span>}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={selectedDate || undefined}
              onSelect={handleSelect}
              disabled={isDateDisabled}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        {allowClear && selectedDate && !disabled && (
          <button
            onClick={handleClear}
            className="ml-1 flex shrink-0 items-center justify-center p-1 text-ink-muted transition-colors hover:text-ink-secondary"
            title="Clear Date"
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default SingleDatePicker;
